import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../context/NotificationContext';
import { useTheme } from '../context/ThemeContext';
import { formatDistanceToNow } from 'date-fns';

const NotificationBell: React.FC = () => {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Safely use notifications context
  let notifications: any[] = [];
  let unreadCount = 0;
  let markAsRead = (id: string) => {};
  let markAllAsRead = () => {};
  let clearAll = () => {};

  try {
    const notificationContext = useNotifications();
    notifications = notificationContext.notifications;
    unreadCount = notificationContext.unreadCount;
    markAsRead = notificationContext.markAsRead;
    markAllAsRead = notificationContext.markAllAsRead;
    clearAll = notificationContext.clearAll;
  } catch (error) {
    console.warn('NotificationContext not available, using defaults');
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen && unreadCount > 0) {
      // Mark visible notifications as read after a delay
      setTimeout(() => {
        notifications.slice(0, 10).forEach(n => {
          if (!n.read) markAsRead(n.id);
        });
      }, 2000);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'kot': return '🧾';
      case 'order': return '🛍️';
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      default: return 'ℹ️';
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'kot': return theme.primary || 'var(--action)';
      case 'order': return theme.success || '#16A34A';
      case 'success': return theme.success || '#16A34A';
      case 'error': return theme.danger || '#DC2626';
      case 'warning': return theme.warning || '#D97706';
      default: return theme.primary || 'var(--action)';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={toggleDropdown}
        className="relative p-2 rounded-lg transition-colors"
        style={{
          backgroundColor: isOpen ? theme.primary + '20' : 'transparent',
          color: theme.text
        }}
        onMouseOver={(e) => {
          if (!isOpen) {
            e.currentTarget.style.backgroundColor = theme.primary + '10';
          }
        }}
        onMouseOut={(e) => {
          if (!isOpen) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white animate-pulse"
            style={{ backgroundColor: theme.danger || '#DC2626' }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="fixed sm:absolute right-2 sm:right-0 left-2 sm:left-auto mt-2 sm:w-96 max-h-[600px] rounded-lg shadow-xl overflow-hidden z-50"
          style={{
            backgroundColor: theme.components.card.background,
            border: `1px solid ${theme.components.card.border}`,
            boxShadow: `0 10px 40px ${theme.primary}20`,
            top: window.innerWidth < 640 ? '60px' : 'auto'
          }}
        >
          {/* Header */}
          <div
            className="p-3 sm:p-4 border-b flex items-center justify-between"
            style={{ borderColor: theme.components.card.border }}
          >
            <h3 className="text-base sm:text-lg font-semibold" style={{ color: theme.text }}>
              Notifications
            </h3>
            <div className="flex items-center gap-1 sm:gap-2">
              {notifications.length > 0 && (
                <>
                  <button
                    onClick={markAllAsRead}
                    className="text-xs sm:text-sm px-2 sm:px-3 py-1 rounded transition-colors"
                    style={{
                      color: theme.primary,
                      backgroundColor: theme.primary + '10'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = theme.primary + '20';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = theme.primary + '10';
                    }}
                  >
                    <span className="hidden sm:inline">Mark all read</span>
                    <span className="sm:hidden">Read all</span>
                  </button>
                  <button
                    onClick={clearAll}
                    className="text-xs sm:text-sm px-2 sm:px-3 py-1 rounded transition-colors"
                    style={{
                      color: theme.danger || '#DC2626',
                      backgroundColor: (theme.danger || '#DC2626') + '10'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = (theme.danger || '#DC2626') + '20';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = (theme.danger || '#DC2626') + '10';
                    }}
                  >
                    <span className="hidden sm:inline">Clear all</span>
                    <span className="sm:hidden">Clear</span>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto max-h-[500px]">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-5xl mb-4">🔔</div>
                <p style={{ color: theme.gray[600] }}>No notifications yet</p>
              </div>
            ) : (
              <div>
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-3 sm:p-4 border-b transition-colors cursor-pointer ${
                      !notification.read ? 'bg-opacity-10' : ''
                    }`}
                    style={{
                      borderColor: theme.components.card.border,
                      backgroundColor: !notification.read ? theme.primary + '05' : 'transparent'
                    }}
                    onClick={() => markAsRead(notification.id)}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = theme.primary + '10';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = !notification.read ? theme.primary + '05' : 'transparent';
                    }}
                  >
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div
                        className="text-xl sm:text-2xl flex-shrink-0"
                        style={{ color: getNotificationColor(notification.type) }}
                      >
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1">
                          <h4 className="font-semibold text-sm truncate" style={{ color: theme.text }}>
                            {notification.title}
                          </h4>
                          <span className="text-xs" style={{ color: theme.gray[500] }}>
                            {formatDistanceToNow(notification.timestamp, { addSuffix: true })}
                          </span>
                        </div>
                        <p className="text-xs sm:text-sm break-words" style={{ color: theme.gray[600] }}>
                          {notification.message}
                        </p>
                        {notification.actions && notification.actions.length > 0 && (
                          <div className="mt-2 flex gap-2">
                            {notification.actions.map((action, index) => (
                              <button
                                key={index}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  action.action();
                                }}
                                className="text-xs px-2 py-1 rounded transition-colors"
                                style={{
                                  backgroundColor: theme.primary + '20',
                                  color: theme.primary
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor = theme.primary + '30';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.backgroundColor = theme.primary + '20';
                                }}
                              >
                                {action.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {!notification.read && (
                        <div
                          className="w-2 h-2 rounded-full animate-pulse"
                          style={{ backgroundColor: theme.primary }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;